import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Coupon } from './paymentMethod';
import { formatDateToDotShort } from '../../func/func';

interface Props {
  couponList: Coupon[];
  selectedCoupon: Coupon | null;
  originPrice: number;
  setSelectedCoupon: (c: Coupon | null) => void;
  onClose: () => void;
}

function CouponModal({
  couponList,
  selectedCoupon,
  originPrice,
  setSelectedCoupon,
  onClose,
}: Props) {
  const [selected, setSelected] = useState(selectedCoupon);

  const discountPrice = (price: number) => {
    return originPrice - price;
  };

  const sortedCouponList = useMemo(() => {
    return [...couponList].sort((a, b) => {
      if (a.is_possible && !b.is_possible) return -1;
      if (!a.is_possible && b.is_possible) return 1;

      // 할인 금액 높은 순 (내림차순)
      if (a.payment_price < b.payment_price) return -1;
      if (a.payment_price > b.payment_price) return 1;
      return 0;
    });
  }, [couponList]);

  const handleClickCard = (card: Coupon) => {
    if (selected && selected.coupon_history_id === card.coupon_history_id) {
      setSelected(null);
    } else if (card.is_possible) {
      setSelected(card);
    }
  };

  return (
    <ModalContainer>
      <Header>
        <BackButton onClick={onClose}>
          <img src="/assets/images/arrow/back-arrow-left-gray.svg" alt="" />
        </BackButton>
        <HeaderTitle>할인쿠폰</HeaderTitle>
      </Header>
      <Content>
        <CouponList>
          {sortedCouponList.map((item) => (
            <CouponItem
              key={item.coupon_history_id}
              onClick={() => handleClickCard(item)}
              $active={selected?.coupon_history_id === item.coupon_history_id}
              $disabled={!item.is_possible}
            >
              <CouponContent>
                <TextArea>
                  <CouponTitle $disabled={!item.is_possible}>
                    {item.name}
                  </CouponTitle>
                  {!item.name.includes('연장') && (
                    <Price $disabled={!item.is_possible}>
                      {/* {discountPrice(item.payment_price).toLocaleString(
                        'ko-KR',
                      )}
                      원 할인 */}
                      {item.membership}
                    </Price>
                  )}
                </TextArea>
                <TextArea $disabled={!item.is_possible}>
                  <div>{item.description}</div>
                  <div>{formatDateToDotShort(item.expire_time)}까지</div>
                </TextArea>
              </CouponContent>
              <CouponCheckbox>
                {item.is_possible ? (
                  <img
                    src={`/assets/images/check/circle-checked-${selected?.coupon_history_id === item.coupon_history_id ? 'blue' : 'gray'}.svg`}
                    alt=""
                  />
                ) : (
                  <DisableTextBox>
                    <div>
                      사용
                      <br />
                      불가
                    </div>
                  </DisableTextBox>
                )}
              </CouponCheckbox>
            </CouponItem>
          ))}
        </CouponList>
      </Content>
      <ButtonBox>
        <CancleButton onClick={onClose}>닫기</CancleButton>
        <SubmitButton
          // $disable={!selected}
          onClick={() => {
            setSelectedCoupon(selected);
            onClose();
          }}
        >
          사용하기
        </SubmitButton>
      </ButtonBox>
    </ModalContainer>
  );
}

export default CouponModal;

const ModalContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #ffffff;
  z-index: 2000;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  width: 100%;
  padding: 6px;
  align-items: center;
  gap: 2px;
  border-bottom: 1px solid #e5e8ea;
`;

const BackButton = styled.div`
  display: flex;
  padding: 10px;
  align-items: center;
`;

const HeaderTitle = styled.div`
  display: flex;
  height: 44px;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;

  color: #222;
  font-size: 16px;
  font-weight: 700;
  line-height: 145%;
  letter-spacing: -0.32px;
`;

const Content = styled.div`
  display: flex;
  padding: 24px 16px;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  align-self: stretch;
  flex: 1 0 0;
`;

const CouponList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  align-self: stretch;
`;

const CouponItem = styled.div<{ $active?: boolean; $disabled?: boolean }>`
  display: flex;
  padding: 16px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  border-radius: 12px;
  border-width: 1px;
  border-style: solid;
  border-color: ${({ $active, $disabled }) =>
    $active && !$disabled ? '#008AFF' : '#E1E2E4'};
  background: #fff;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
`;

const CouponCheckbox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;
`;

const CouponContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
`;

const TextArea = styled.div<{ $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  color: ${({ $disabled }) => ($disabled ? '#37383C29' : '#616161')};
  font-size: 12px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.24px;
`;

const CouponTitle = styled.div<{ $disabled?: boolean }>`
  color: ${({ $disabled }) => ($disabled ? '#37383C47' : '#008aff')};
  font-size: 18px;
  font-weight: 700;
  line-height: 145%;
  letter-spacing: -0.36px;
`;

const Price = styled.div<{ $disabled?: boolean }>`
  color: ${({ $disabled }) => ($disabled ? '#37383C47' : '#222')};
  font-size: 15px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.3px;
`;

const ButtonBox = styled.div`
  display: flex;
  width: 100%;
  height: 94px;
  padding: 0 16px 24px 16px;
  justify-content: center;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
`;

const Button = styled.button`
  display: flex;
  padding: 14px 28px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: none;

  font-weight: 500;
  font-size: 16px;
  line-height: 145%;
`;

const CancleButton = styled(Button)`
  background-color: #70737c14;
  color: #616161;
`;

const SubmitButton = styled(Button)<{ $disable?: boolean }>`
  flex: 1 0 0;
  background-color: ${({ $disable }) => ($disable ? '#F7F7F8' : '#008AFF')};
  color: ${({ $disable }) => ($disable ? '#37383C29' : '#FFFFFF')};
`;

const DisableTextBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;

  color: #616161;
  text-align: center;
  font-size: 13px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.26px;
`;
